/**
 * BuscopisPage.jsx
 * Assegura't d'importar les fonts al teu index.html o _document.js:
 * <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=DM+Sans:wght@300;400;500;600&display=swap" rel="stylesheet" />
 */

import { useState } from "react";
import { Box, Container, Typography, Button, Grid, GlobalStyles } from "@mui/material";

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  black:      "#111111",
  cream:      "#F8F5FF",
  green:      "#7B2FBE",
  greenLight: "#9644D8",
  gold:       "#00D4E8",
  white:      "#ffffff",
  grey:       "#6B6B6B",
  border:     "#E0D5F5",
};
const playfair = "'Playfair Display', serif";
const dmSans   = "'DM Sans', sans-serif";
const grad     = "linear-gradient(135deg, #00C4D4 0%, #7C2FBE 100%)";
const gradSoft = "linear-gradient(135deg, rgba(0,196,212,0.12) 0%, rgba(124,47,190,0.12) 100%)";
const syne     = "'Syne', sans-serif";

// ─── Traduccions ──────────────────────────────────────────────────────────────
const translations = {
  ca: {
    nav_canal:        "Subscriu-te al canal",
    nav_form:         "Omple el formulari",
    hero_tag:         "Servei gratuït · Andorra",
    hero_title:       "Trobar pis a Andorra<br>no hauria de ser<br><em>tan difícil.</em>",
    hero_sub:         "Més de 8.000 anuncis repartits per Idealista, Habitaclia, Fotocasa i Pisos.ad. Preus contradictoris, pisos duplicats, hores perdudes. Nosaltres ho fem per tu — i t'avisem si trobem el que encaixa.",
    hero_btn1:        "Omple el formulari i despreocupa't",
    hero_btn2:        "Subscriu-te al canal de propietats",
    hero_note:        "Tot <strong>gratuït</strong>. Sense spam. Sense compromís.",
    hero_right_title: "El mercat immobiliari a Andorra",
    stat1:            "Anuncis actius als portals",
    stat2:            "Portals principals a revisar",
    stat3:            "Anuncis duplicats entre portals",
    stat4:            "Cost del servei Buscopis",
    prob_tag:         "El problema",
    prob_title:       "El mercat és un <em>caos.</em>",
    prob_sub:         "Buscar pis a Andorra vol dir hores davant la pantalla, comparar preus que no quadren i no saber si el que veus és real o duplicat.",
    p1_title:         "Portals fragmentats",
    p1_desc:          "El mateix pis apareix a tres portals amb preus diferents. Impossible saber quin és el correcte.",
    p2_title:         "Temps perdut",
    p2_desc:          "Hores setmanals consultant portals per no trobar res nou. La majoria d'anuncis ja els has vist.",
    p3_title:         "Informació incompleta",
    p3_desc:          "Fotos de mala qualitat, preus diferents, descripcions vagues. No saps si val la pena trucar.",
    how_tag:          "Com funciona",
    how_title:        "Simple com hauria de ser",
    how_sub:          "Dos minuts del teu temps. Nosaltres fem la resta.",
    s1_title:         "Omples el formulari",
    s1_desc:          "Zona, metres, pressupost, habitacions. Un minut i ja ho tenim.",
    s2_title:         "Nosaltres busquem",
    s2_desc:          "Filtrem tots els portals i descartem el soroll. Cada dia.",
    s3_title:         "T'avisem si hi ha alguna cosa",
    s3_desc:          "Si trobem un pis que encaixa, t'avisem. Si no, no et molestem.",
    free_badge:       "Tot <strong>completament gratuït</strong> per als compradors · Sempre.",
    ch_tag:           "Canal de propietats",
    ch_title:         "Rep propietats <em>seleccionades</em> sense buscar res",
    ch_sub:           "Publiquem propietats que realment valen la pena. Si t'interessa, ens escrius amb el codi.",
    btn_wa:           "Canal de WhatsApp",
    btn_tg:           "Canal de Telegram",
    final_title:      "Deixa de buscar.<br><em>Nosaltres busquem.</em>",
    final_sub:        "Omple el formulari en menys de dos minuts i oblida't del tema. T'avisem quan aparegui alguna cosa que encaixi.",
    final_btn:        "Vull que em busqueu un pis",
    final_note:       "Gratuït · Sense spam · Sense compromís",
    foot_form:        "Formulari",
    foot_canal:       "Canal propietats",
    path1_title: "Omple el formulari",
    path1_desc:  "Digue'ns el que busques. Nosaltres filtrem i t'avisem quan aparegui.",
    path2_title: "Subscriu-te al canal",
    path2_desc:  "Rep propietats seleccionades directament al mòbil, sense buscar.",
  },
  es: {
    nav_canal:        "Suscribirme al canal",
    nav_form:         "Rellenar formulario",
    hero_tag:         "Servicio gratuito · Andorra",
    hero_title:       "Encontrar piso en Andorra<br>no debería ser<br><em>tan difícil.</em>",
    hero_sub:         "Más de 8.000 anuncios repartidos por Idealista, Habitaclia, Fotocasa y Pisos.ad. Precios contradictorios, pisos duplicados, horas perdidas. Nosotros lo hacemos por ti — y te avisamos si encontramos lo que buscas.",
    hero_btn1:        "Rellena el formulario y despreocúpate",
    hero_btn2:        "Suscríbete al canal de propiedades",
    hero_note:        "Todo <strong>gratuito</strong>. Sin spam. Sin compromiso.",
    hero_right_title: "El mercado inmobiliario en Andorra",
    stat1:            "Anuncios activos en los portales",
    stat2:            "Portales principales a revisar",
    stat3:            "Anuncios duplicados entre portales",
    stat4:            "Coste del servicio Buscopis",
    prob_tag:         "El problema",
    prob_title:       "El mercado es un <em>caos.</em>",
    prob_sub:         "Buscar piso en Andorra significa horas delante de la pantalla, comparar precios que no cuadran y no saber si lo que ves es real o duplicado.",
    p1_title:         "Portales fragmentados",
    p1_desc:          "El mismo piso aparece en tres portales con precios distintos. Imposible saber cuál es el correcto.",
    p2_title:         "Tiempo perdido",
    p2_desc:          "Horas semanales consultando portales sin encontrar nada nuevo. La mayoría de anuncios ya los has visto.",
    p3_title:         "Información incompleta",
    p3_desc:          "Fotos de mala calidad, precios diferentes, descripciones vagas. No sabes si vale la pena llamar.",
    how_tag:          "Cómo funciona",
    how_title:        "Simple como debería ser",
    how_sub:          "Dos minutos de tu tiempo. Nosotros hacemos el resto.",
    s1_title:         "Rellenas el formulario",
    s1_desc:          "Zona, metros, presupuesto, habitaciones. Un minuto y listo.",
    s2_title:         "Nosotros buscamos",
    s2_desc:          "Filtramos todos los portales y descartamos el ruido. Cada día.",
    s3_title:         "Te avisamos si hay algo",
    s3_desc:          "Si encontramos un piso que encaja, te avisamos. Si no, no te molestamos.",
    free_badge:       "Todo <strong>completamente gratuito</strong> para compradores · Siempre.",
    ch_tag:           "Canal de propiedades",
    ch_title:         "Recibe propiedades <em>seleccionadas</em> sin buscar nada",
    ch_sub:           "Publicamos propiedades que realmente valen la pena. Si te interesa, nos escribes con el código.",
    btn_wa:           "Canal de WhatsApp",
    btn_tg:           "Canal de Telegram",
    final_title:      "Deja de buscar.<br><em>Nosotros buscamos.</em>",
    final_sub:        "Rellena el formulario en menos de dos minutos y olvídate del tema. Te avisamos cuando aparezca algo que encaje.",
    final_btn:        "Quiero que me busquéis un piso",
    final_note:       "Gratuito · Sin spam · Sin compromiso",
    foot_form:        "Formulario",
    foot_canal:       "Canal propiedades",
    path1_title: "Rellena el formulario",
    path1_desc:  "Dinos lo que buscas. Nosotros filtramos y te avisamos cuando aparezca.",
    path2_title: "Suscríbete al canal",
    path2_desc:  "Recibe propiedades seleccionadas directamente en el móvil, sin buscar.",
  },
  fr: {
    nav_canal:        "S'abonner au canal",
    nav_form:         "Remplir le formulaire",
    hero_tag:         "Service gratuit · Andorre",
    hero_title:       "Trouver un appartement en Andorre<br>ne devrait pas être<br><em>si difficile.</em>",
    hero_sub:         "Plus de 8 000 annonces réparties sur Idealista, Habitaclia, Fotocasa et Pisos.ad. Prix contradictoires, appartements en double, heures perdues. Nous le faisons pour vous — et vous prévenons dès que quelque chose correspond.",
    hero_btn1:        "Remplissez le formulaire et oubliez",
    hero_btn2:        "Abonnez-vous au canal de propriétés",
    hero_note:        "Tout <strong>gratuit</strong>. Sans spam. Sans engagement.",
    hero_right_title: "Le marché immobilier en Andorre",
    stat1:            "Annonces actives sur les portails",
    stat2:            "Principaux portails à consulter",
    stat3:            "Annonces dupliquées entre portails",
    stat4:            "Coût du service Buscopis",
    prob_tag:         "Le problème",
    prob_title:       "Le marché est un <em>chaos.</em>",
    prob_sub:         "Chercher un appartement en Andorre signifie des heures devant l'écran, comparer des prix qui ne correspondent pas et ne pas savoir si ce que vous voyez est réel ou dupliqué.",
    p1_title:         "Portails fragmentés",
    p1_desc:          "Le même appartement apparaît sur trois portails avec des prix différents. Impossible de savoir lequel est correct.",
    p2_title:         "Temps perdu",
    p2_desc:          "Des heures par semaine à consulter des portails sans rien trouver de nouveau. La plupart des annonces, vous les avez déjà vues.",
    p3_title:         "Informations incomplètes",
    p3_desc:          "Photos de mauvaise qualité, prix différents, descriptions vagues. Vous ne savez pas si ça vaut la peine d'appeler.",
    how_tag:          "Comment ça marche",
    how_title:        "Simple comme ça devrait l'être",
    how_sub:          "Deux minutes de votre temps. Nous faisons le reste.",
    s1_title:         "Vous remplissez le formulaire",
    s1_desc:          "Zone, superficie, budget, chambres. Une minute et c'est fait.",
    s2_title:         "Nous cherchons",
    s2_desc:          "Nous filtrons tous les portails et éliminons le bruit. Chaque jour.",
    s3_title:         "Nous vous alertons si quelque chose correspond",
    s3_desc:          "Si nous trouvons un appartement qui correspond, nous vous prévenons. Sinon, nous ne vous dérangeons pas.",
    free_badge:       "Tout <strong>entièrement gratuit</strong> pour acheteurs · Toujours.",
    ch_tag:           "Canal de propriétés",
    ch_title:         "Recevez des propriétés <em>sélectionnées</em> sans chercher",
    ch_sub:           "Nous publions des propriétés qui valent vraiment la peine. Si ça vous intéresse, écrivez-nous avec le code.",
    btn_wa:           "Canal WhatsApp",
    btn_tg:           "Canal Telegram",
    final_title:      "Arrêtez de chercher.<br><em>Nous cherchons.</em>",
    final_sub:        "Remplissez le formulaire en moins de deux minutes et oubliez le sujet. Nous vous alertons quand quelque chose correspond.",
    final_btn:        "Je veux que vous cherchiez pour moi",
    final_note:       "Gratuit · Sans spam · Sans engagement",
    foot_form:        "Formulaire",
    foot_canal:       "Canal propriétés",
    path1_title: "Remplissez le formulaire",
    path1_desc:  "Dites-nous ce que vous cherchez. Nous filtrons et vous alertons dès que quelque chose correspond.",
    path2_title: "Abonnez-vous au canal",
    path2_desc:  "Recevez des propriétés sélectionnées directement sur votre téléphone, sans chercher.",
  },
  en: {
    nav_canal:        "Subscribe to channel",
    nav_form:         "Fill in the form",
    hero_tag:         "Free service · Andorra",
    hero_title:       "Finding a flat in Andorra<br>shouldn't be<br><em>this hard.</em>",
    hero_sub:         "Over 8,000 listings spread across Idealista, Habitaclia, Fotocasa and Pisos.ad. Contradictory prices, duplicated flats, wasted hours. We do it for you — and alert you when we find something that fits.",
    hero_btn1:        "Fill in the form and relax",
    hero_btn2:        "Subscribe to the property channel",
    hero_note:        "All <strong>free</strong>. No spam. No commitment.",
    hero_right_title: "The real estate market in Andorra",
    stat1:            "Active listings on portals",
    stat2:            "Main portals to check",
    stat3:            "Duplicated listings across portals",
    stat4:            "Cost of the Buscopis service",
    prob_tag:         "The problem",
    prob_title:       "The market is a <em>mess.</em>",
    prob_sub:         "Looking for a flat in Andorra means hours in front of a screen, comparing prices that don't add up and not knowing if what you see is real or a duplicate.",
    p1_title:         "Fragmented portals",
    p1_desc:          "The same flat appears on three portals with different prices. Impossible to know which one is correct.",
    p2_title:         "Wasted time",
    p2_desc:          "Hours every week checking portals and finding nothing new. You've already seen most of the listings.",
    p3_title:         "Incomplete information",
    p3_desc:          "Poor quality photos, different prices, vague descriptions. You don't know if it's worth calling.",
    how_tag:          "How it works",
    how_title:        "Simple as it should be",
    how_sub:          "Two minutes of your time. We do the rest.",
    s1_title:         "Fill in the form",
    s1_desc:          "Area, size, budget, rooms. One minute and we're ready.",
    s2_title:         "We search",
    s2_desc:          "We filter all the portals and cut through the noise. Every day.",
    s3_title:         "We alert you if there's a match",
    s3_desc:          "If we find a flat that fits, we'll let you know. If not, we won't bother you.",
    free_badge:       "<strong>Completely free</strong> for buyers · Always.",
    ch_tag:           "Property channel",
    ch_title:         "Receive <em>curated</em> properties without searching",
    ch_sub:           "We post properties that are genuinely worth it. If you're interested, message us with the code.",
    btn_wa:           "WhatsApp Channel",
    btn_tg:           "Telegram Channel",
    final_title:      "Stop searching.<br><em>We search.</em>",
    final_sub:        "Fill in the form in under two minutes and forget about it. We'll alert you when something that fits comes up.",
    final_btn:        "Find me a flat",
    final_note:       "Free · No spam · No commitment",
    foot_form:        "Form",
    foot_canal:       "Property channel",
    path1_title: "Fill in the form",
    path1_desc:  "Tell us what you're looking for. We filter and alert you when something comes up.",
    path2_title: "Subscribe to the channel",
    path2_desc:  "Receive curated properties directly on your phone, without searching.",
  },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Renderitza text amb HTML inline (em, strong, br) de forma segura */
const Html = ({ html, component = "span", sx = {} }) => (
  <Box
    component={component}
    dangerouslySetInnerHTML={{ __html: html }}
    sx={{
      "& em":     { fontStyle: "italic", color: C.green },
      "& strong": { fontWeight: 700 },
      ...sx,
    }}
  />
);

/** Selector de llengua */
const LangSwitcher = ({ lang, onChange }) => {
  const langs = ["ca", "es", "fr", "en"];
  return (
    <Box
      sx={{
        display: "flex", alignItems: "center", gap: "2px",
        background: "rgba(123,47,190,0.07)",
        borderRadius: "100px", padding: "4px",
      }}
    >
      {langs.map((l) => (
        <Box
          key={l}
          component="button"
          onClick={() => onChange(l)}
          sx={{
            border: "none",
            background: lang === l ? C.green : "transparent",
            color: lang === l ? C.white : C.grey,
            fontFamily: dmSans,
            fontSize: "0.72rem", fontWeight: 600,
            padding: "4px 8px", borderRadius: "100px",
            cursor: "pointer", letterSpacing: "0.04em",
            transition: "all 0.2s",
            textTransform: "uppercase",
            "&:hover": { color: lang === l ? C.white : C.green },
          }}
        >
          {l}
        </Box>
      ))}
    </Box>
  );
};

/** Targeta d'estadística del hero dret */
const StatCard = ({ num, label, corners }) => (
  <Box
    sx={{
      background: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.1)",
      padding: "28px 24px",
      borderRadius: corners,
      transition: "background 0.2s",
      "&:hover": { background: "rgba(255,255,255,0.10)" },
    }}
  >
    <Typography
      sx={{
        fontFamily: playfair, fontSize: "2.4rem", fontWeight: 900,
        color: C.gold, lineHeight: 1, mb: "8px",
      }}
    >
      {num}
    </Typography>
    <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.65)", fontWeight: 300, lineHeight: 1.4 }}>
      {label}
    </Typography>
  </Box>
);

/** Pas del "Com funciona" */
const Step = ({ num, title, desc }) => (
  <Box sx={{ textAlign: "center", px: { xs: 2, md: 4 } }}>
    <Box
      sx={{
        width: 56, height: 56, borderRadius: "50%",
        background: C.green, color: C.white,
        fontFamily: playfair, fontWeight: 900, fontSize: "1.3rem",
        display: "flex", alignItems: "center", justifyContent: "center",
        margin: "0 auto", mb: 3, position: "relative", zIndex: 1,
      }}
    >
      {num}
    </Box>
    <Typography sx={{ fontSize: "1rem", fontWeight: 600, mb: "10px", fontFamily: dmSans }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: "0.875rem", color: C.grey, lineHeight: 1.6, fontWeight: 300, fontFamily: dmSans }}>
      {desc}
    </Typography>
  </Box>
);

/** Fila del mock post del canal */
const MockRow = ({ emoji, text }) => (
  <Box sx={{ display: "flex", gap: "6px", alignItems: "baseline", my: "4px" }}>
    <span style={{ fontSize: "0.85rem" }}>{emoji}</span>
    <Typography sx={{ fontSize: "0.875rem", color: "rgba(255,255,255,0.85)", fontFamily: dmSans }}>
      {text}
    </Typography>
  </Box>
);

// ─── Pàgina principal ─────────────────────────────────────────────────────────
export const HomePage = () => {
  const [lang, setLang] = useState("ca");
  const t = translations[lang];

  return (
    <>
      {/* Fonts globals */}
      <GlobalStyles
        styles={{
          body: { fontFamily: dmSans, background: C.cream, color: C.black, overflowX: "hidden" },
          "html": { scrollBehavior: "smooth" },
        }}
      />

      {/* ── NAV ──────────────────────────────────────────────────────────── */}
      <Box
        component="nav"
        sx={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: { xs: "14px 20px", md: "16px 48px" },
          background: "rgba(248,245,255,0.95)",
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid ${C.border}`,
          gap: 2,
        }}
      >
        {/* Logo */}
        <Box
          component="a"
          href="/"
          sx={{
            fontFamily: playfair, fontSize: "1.4rem", fontWeight: 900,
            color: C.green, letterSpacing: "-0.02em",
            textDecoration: "none", flexShrink: 0,
          }}
        >
          <img
            src="/logo_1.png"
            alt="Logo"
            style={{ height: "30px", verticalAlign: "middle" }}
          />
        </Box>
        {/* Nav dret */}
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "6px", md: "10px" }, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <LangSwitcher lang={lang} onChange={setLang} />

          <Box
            component="a"
            href="#canal"
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              background: "transparent", color: C.green,
              border: `1.5px solid ${C.green}`, borderRadius: "100px",
              padding: { xs: "8px 14px", md: "8px 20px" },
              fontFamily: dmSans, fontSize: { xs: "0.78rem", md: "0.82rem" }, fontWeight: 500,
              cursor: "pointer", textDecoration: "none", whiteSpace: "nowrap",
              transition: "all 0.2s",
              "&:hover": { background: C.green, color: C.white, transform: "translateY(-1px)" },
            }}
          >
            {t.nav_canal}
          </Box>

          <Box
            component="a"
            href="/busco-pis"              
            sx={{
              background: C.green, color: C.white,
              border: "none", borderRadius: "100px",
              padding: { xs: "8px 14px", md: "9px 20px" },
              fontFamily: dmSans, fontSize: { xs: "0.78rem", md: "0.82rem" }, fontWeight: 500,
              cursor: "pointer", textDecoration: "none", whiteSpace: "nowrap",
              transition: "all 0.2s",
              "&:hover": { background: C.greenLight, transform: "translateY(-1px)" },
            }}
          >
            {t.nav_form}
          </Box>
        </Box>
      </Box>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
       <Box
          component="section"
          sx={{
            minHeight: "100vh",
            display: "flex", flexDirection: "column", justifyContent: "center",
            padding: { xs: "100px 20px 60px", md: "120px 40px 60px" },
            maxWidth: 1100, mx: "auto",
          }}
        >
          {/* Tag pulsant */}
          <Box
            sx={{
              display: "inline-flex", alignItems: "center", gap: "8px",
              background: gradSoft,
              border: "1px solid rgba(0,196,212,0.25)",
              color: C.accent2,
              fontSize: "0.8rem", fontWeight: 500, fontFamily: dmSans,
              padding: "6px 14px", borderRadius: "100px",
              mb: 4, width: "fit-content",
              mt: { xs: 3, md: 0 },
              letterSpacing: "0.03em", textTransform: "uppercase",
              animation: "heroFadeUp 0.5s ease both",
              "&::before": {
                content: '""',
                width: 7, height: 7, borderRadius: "50%",
                background: grad, flexShrink: 0,
                animation: "heroPulse 2s infinite",
              },
            }}
          >
            {t.hero_tag}
          </Box>
      
          {/* Títol — conserva "Trobar pis a Andorra no hauria de ser tan difícil." */}
          <Box
            component="h1"
            dangerouslySetInnerHTML={{ __html: t.hero_title }}
            sx={{
              fontFamily: playfair,
              fontSize: "clamp(2.8rem, 6vw, 5rem)",
              fontWeight: 800, lineHeight: 1.05, letterSpacing: "-0.03em",
              color: C.dark, mb: 3, mt: 0,
              animation: "heroFadeUp 0.5s 0.1s ease both",
              "& em": {
                fontStyle: "normal",
                background: grad,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              },
            }}
          />
      
          {/* Subtítol */}
          <Box
            component="p"
            sx={{
              fontFamily: dmSans, fontSize: "1.15rem",
              color: C.mid, maxWidth: 560, lineHeight: 1.65,
              mb: 6, mt: 0, fontWeight: 300,
              animation: "heroFadeUp 0.5s 0.2s ease both",
            }}
          >
            {t.hero_sub}
          </Box>
      
          {/* Dues opcions */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: "16px", maxWidth: 660,
              animation: "heroFadeUp 0.5s 0.3s ease both",
            }}
          >
            {/* Opció 1 — Formulari (gradient) */}
            <Box
              component="a"
              href="/busco-pis"
              sx={{
                display: "block", borderRadius: "16px", padding: "28px",
                background: grad, color: "white",
                textDecoration: "none",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 16px 40px rgba(124,47,190,0.28)",
                },
              }}
            >
              <Box sx={{ fontSize: "2rem", mb: "14px" }}>📋</Box>
              <Box sx={{ fontFamily: playfair, fontWeight: 700, fontSize: "1.05rem", mb: "6px" }}>
                {t.path1_title}
              </Box>
              <Box sx={{ fontSize: "0.85rem", lineHeight: 1.5, opacity: 0.82, fontFamily: dmSans }}>
                {t.path1_desc}
              </Box>
              <Box sx={{ display: "inline-block", mt: 2, fontSize: "0.8rem", fontWeight: 500, fontFamily: dmSans }}>
                Comença →
              </Box>
            </Box>
      
            {/* Opció 2 — Canal (blanc) */}
            <Box
              component="a"
              href="#canal"
              sx={{
                display: "block", borderRadius: "16px", padding: "28px",
                background: C.card, color: C.dark,
                border: `1.5px solid ${C.border}`,
                textDecoration: "none",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 16px 40px rgba(124,47,190,0.15)",
                },
              }}
            >
              <Box sx={{ fontSize: "2rem", mb: "14px" }}>🔔</Box>
              <Box sx={{ fontFamily: playfair, fontWeight: 700, fontSize: "1.05rem", mb: "6px" }}>
                {t.path2_title}
              </Box>
              <Box sx={{ fontSize: "0.85rem", lineHeight: 1.5, color: C.mid, fontFamily: dmSans }}>
                {t.path2_desc}
              </Box>
              <Box
                sx={{
                  display: "inline-block", mt: 2,
                  fontSize: "0.8rem", fontWeight: 500, fontFamily: dmSans,
                  background: grad,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Veure opcions →
              </Box>
            </Box>
          </Box>
      
          <style>{`
            @keyframes heroFadeUp {
              from { opacity: 0; transform: translateY(24px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            @keyframes heroPulse {
              0%, 100% { opacity: 1; transform: scale(1); }
              50%       { opacity: 0.5; transform: scale(0.85); }
            }
          `}</style>
        </Box>

      {/* ── EL PROBLEMA ──────────────────────────────────────────────────── */}
      <Box
        component="section"
        sx={{
          background: C.white,
          padding: { xs: "70px 24px", md: "100px 48px" },
          display: "flex", flexDirection: "column", alignItems: "center",
        }}
      >
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: C.gold, mb: "20px", textAlign: "center", fontFamily: dmSans }}>
          {t.prob_tag}
        </Typography>

        <Typography
          component="h2"
          sx={{
            fontFamily: playfair,
            fontSize: "clamp(2rem, 3.5vw, 3rem)",
            fontWeight: 900, lineHeight: 1.15,
            letterSpacing: "-0.02em",
            textAlign: "center", maxWidth: 700, mb: "20px",
            "& em": { fontStyle: "italic", color: C.green },
          }}
          dangerouslySetInnerHTML={{ __html: t.prob_title }}
        />

        <Typography sx={{ color: C.grey, textAlign: "center", maxWidth: 560, fontSize: "1rem", lineHeight: 1.7, fontWeight: 300, fontFamily: dmSans }}>
          {t.prob_sub}
        </Typography>

        {/* Targetes de problemes */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: "1px", mt: "60px", width: "100%", maxWidth: 900,
            background: C.border, borderRadius: "16px", overflow: "hidden",
          }}
        >
          {[
            { icon: "📑", title: t.p1_title, desc: t.p1_desc },
            { icon: "⏱️", title: t.p2_title, desc: t.p2_desc },
            { icon: "🤷", title: t.p3_title, desc: t.p3_desc },
          ].map(({ icon, title, desc }) => (
            <Box
              key={title}
              sx={{
                background: C.cream, padding: "36px 32px",
                transition: "background 0.2s",
                "&:hover": { background: C.white },
              }}
            >
              <Typography sx={{ fontSize: "1.8rem", mb: 2 }}>{icon}</Typography>
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, mb: 1, fontFamily: dmSans }}>{title}</Typography>
              <Typography sx={{ fontSize: "0.875rem", color: C.grey, lineHeight: 1.6, fontWeight: 300, fontFamily: dmSans }}>{desc}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── COM FUNCIONA ─────────────────────────────────────────────────── */}
      <Box
        component="section"
        sx={{ padding: { xs: "70px 24px", md: "100px 48px" }, maxWidth: 1100, mx: "auto" }}
      >
        <Box sx={{ textAlign: "center", mb: "70px" }}>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: C.gold, mb: "20px", fontFamily: dmSans }}>
            {t.how_tag}
          </Typography>
          <Typography
            component="h2"
            sx={{ fontFamily: playfair, fontSize: "clamp(2rem, 3.5vw, 3rem)", fontWeight: 900, letterSpacing: "-0.02em", mb: "12px" }}
          >
            {t.how_title}
          </Typography>
          <Typography sx={{ color: C.grey, fontWeight: 300, fontSize: "1rem", fontFamily: dmSans }}>
            {t.how_sub}
          </Typography>
        </Box>

        {/* Steps amb línia de connexió */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: { xs: "40px", md: 0 },
            position: "relative",
            
          }}
        >
          <Step num="1" title={t.s1_title} desc={t.s1_desc} />
          <Step num="2" title={t.s2_title} desc={t.s2_desc} />
          <Step num="3" title={t.s3_title} desc={t.s3_desc} />
        </Box>

        {/* Free badge */}
        <Box
          sx={{
            display: "inline-block", mt: "60px",
            background: "rgba(123,47,190,0.08)",
            border: "1px solid rgba(123,47,190,0.2)",
            color: C.green, padding: "10px 24px", borderRadius: "100px",
            fontSize: "0.875rem", fontWeight: 500, width: "100%",
            textAlign: "center", fontFamily: dmSans,
            "& strong": { fontWeight: 700 },
          }}
          dangerouslySetInnerHTML={{ __html: t.free_badge }}
        />
      </Box>

      {/* ── CANAL ────────────────────────────────────────────────────────── */}
      <Box
        id="canal"
        component="section"
        sx={{
          background: C.black,
          padding: { xs: "70px 24px", md: "100px 48px" },
          display: "flex", flexDirection: "column", alignItems: "center",
        }}
      >
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: C.gold, mb: "20px", fontFamily: dmSans }}>
          {t.ch_tag}
        </Typography>

        <Typography
          component="h2"
          sx={{
            fontFamily: playfair,
            fontSize: "clamp(2rem, 3.5vw, 3rem)",
            fontWeight: 900, color: C.white,
            textAlign: "center", maxWidth: 680,
            letterSpacing: "-0.02em", lineHeight: 1.1, mb: 2,
            "& em": { fontStyle: "italic", color: C.gold },
          }}
          dangerouslySetInnerHTML={{ __html: t.ch_title }}
        />

        <Typography sx={{ color: "rgba(255,255,255,0.5)", textAlign: "center", maxWidth: 520, fontSize: "1rem", lineHeight: 1.7, fontWeight: 300, mb: 6, fontFamily: dmSans }}>
          {t.ch_sub}
        </Typography>

        {/* Mock post */}
        <Box
          sx={{
            background: "#1E1E1E", border: "1px solid #2E2E2E",
            borderRadius: "20px", padding: "32px",
            maxWidth: 420, width: "100%", mb: 5,
          }}
        >
          {/* Header del post */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "20px" }}>
            <Box
              sx={{
                width: 40, height: 40, borderRadius: "50%",
                background: "linear-gradient(135deg, #00D4E8, #7B2FBE)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1rem", flexShrink: 0,
              }}
            >
              🏠
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: C.white, fontFamily: dmSans }}>
                Buscopis · Canal
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.4)", fontFamily: dmSans }}>
                avui, 10:23
              </Typography>
            </Box>
          </Box>

          {/* Contingut del post */}
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: C.white, mb: 2, fontFamily: dmSans }}>
            📍 Pis al centre d'Escaldes
          </Typography>

          <MockRow emoji="🏠"  text="→ 70 m²" />
          <MockRow emoji="🛏️" text="→ 2 habitacions" />
          <MockRow emoji="🌱"  text="→ Balcó" />
          <MockRow emoji="☀️"  text="→ Orientació sud" />
          <MockRow emoji="🛀"  text="→ 2 banys" />
          <MockRow emoji="🚗"  text="→ 1 plaça d'aparcament" />
          <MockRow emoji="🙅"  text="→ Sense llogaters" />
          <MockRow emoji="💰"  text="→ Entre 500.000 € i 550.000 €" />

          {/* Codi */}
          <Box sx={{ mt: "20px", pt: "20px", borderTop: "1px solid #2E2E2E", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box
              sx={{
                fontFamily: "monospace", fontSize: "0.8rem",
                color: C.gold, background: "rgba(0,212,232,0.12)",
                padding: "4px 10px", borderRadius: "6px",
              }}
            >
              ESCALDES1
            </Box>
            <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.4)", fontFamily: dmSans }}>
              📩 Escriu-nos amb el codi
            </Typography>
          </Box>
        </Box>

        {/* Botons de canal */}
        <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "center" }}>
          <Box
            component="a"
            href="https://whatsapp.com/channel/0029VbC42mkFnSz1HWHLiX0h"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "flex", alignItems: "center", gap: "8px",
              background: "#25D366", color: C.white,
              borderRadius: "12px", padding: "14px 24px",
              fontSize: "0.95rem", fontWeight: 500,
              textDecoration: "none", fontFamily: dmSans,
              transition: "all 0.2s",
              "&:hover": { filter: "brightness(1.1)", transform: "translateY(-2px)" },
            }}
          >
            <span>📱</span>
            <span>{t.btn_wa}</span>
          </Box>

          <Box
            component="a"
            href="https://t.me/buscopis"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "flex", alignItems: "center", gap: "8px",
              background: "#229ED9", color: C.white,
              borderRadius: "12px", padding: "14px 24px",
              fontSize: "0.95rem", fontWeight: 500,
              textDecoration: "none", fontFamily: dmSans,
              transition: "all 0.2s",
              "&:hover": { filter: "brightness(1.1)", transform: "translateY(-2px)" },
            }}
          >
            <span>✈️</span>
            <span>{t.btn_tg}</span>
          </Box>
        </Box>
      </Box>

      {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
      <Box
        id="formulari"
        component="section"
        sx={{
          padding: { xs: "80px 24px", md: "120px 48px" },
          textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center",
          background: C.cream,
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontFamily: playfair,
            fontSize: "clamp(2.5rem, 5vw, 4rem)",
            fontWeight: 900, lineHeight: 1.05,
            letterSpacing: "-0.03em",
            maxWidth: 700, mb: "20px",
            "& em": { fontStyle: "italic", color: C.green },
          }}
          dangerouslySetInnerHTML={{ __html: t.final_title }}
        />

        <Typography sx={{ color: C.grey, fontSize: "1rem", fontWeight: 300, maxWidth: 480, lineHeight: 1.7, mb: "36px", fontFamily: dmSans }}>
          {t.final_sub}
        </Typography>

        <Box
          component="a"
          href="/busco-pis"
          sx={{
            display: "inline-block",
            background: C.green, color: C.white,
            borderRadius: "14px", padding: "18px 40px",
            fontSize: "1.05rem", fontWeight: 500,
            textDecoration: "none", fontFamily: dmSans,
            transition: "all 0.25s",
            "&:hover": { background: C.greenLight, transform: "translateY(-2px)", boxShadow: "0 8px 24px rgba(123,47,190,0.35)" },
          }}
        >
          {t.final_btn}
        </Box>

        <Typography sx={{ mt: 2, fontSize: "0.8rem", color: C.grey, fontFamily: dmSans }}>
          {t.final_note}
        </Typography>
      </Box>

      {/* ── FOOTER ───────────────────────────────────────────────────────── */}
      <Box
        component="footer"
        sx={{
          borderTop: `1px solid ${C.border}`,
          padding: { xs: "24px", md: "32px 48px" },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          gap: { xs: "16px", md: 0 },
          background: C.white,
          textAlign: { xs: "center", md: "left" },
        }}
      >
          <img
            src="/logo_1.png"
            alt="Logo"
            style={{ height: "30px", verticalAlign: "middle" }}
          />

        <Box sx={{ display: "flex", gap: "24px" }}>
          {[
            { href: "/busco-pis", label: t.foot_form },
            { href: "#canal",     label: t.foot_canal },
            { href: "https://instagram.com/buscopis", label: "Instagram", external: true },
          ].map(({ href, label, external }) => (
            <Box
              key={label}
              component="a"
              href={href}
              target={external ? "_blank" : undefined}
              rel={external ? "noopener noreferrer" : undefined}
              sx={{
                fontSize: "0.8rem", color: C.grey,
                textDecoration: "none", fontFamily: dmSans,
                "&:hover": { color: C.green },
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Animació fadeUp global */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default HomePage;
